import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import { href, navigate } from '../../app/router';
import { ApiError } from '../../lib/api';
import type { InvitationPeek, InviteScope } from '../../lib/sharing-types';
import { Button } from '../../ui';
import { AuthLayout } from '../auth/AuthLayout';
import { useSession } from '../auth/session';
import { InviteSignup } from './InviteSignup';
import { Message } from './SharePage';
import { pendingToken, stashToken, takeStashedToken } from './token';

const destination = (s: InviteScope): string =>
  s.type === 'board' ? href('board', { boardId: s.id }) : s.type === 'project' ? href('project', { projectId: s.id }) : href('home');

type Step = 'working' | 'checking' | 'signup' | 'mismatch' | 'invalid' | 'expired' | 'error';

/**
 * /invite#<token>: a real account accepts; only the invited email may. Someone signed out (or a guest) first learns
 * what the invitation is (P17): an address without an account creates it here, with a password of their choice; an
 * address that has one signs in, then accepts.
 */
export function InvitePage() {
  const { t } = useTranslation();
  const api = useApi();
  const session = useSession();
  const queryClient = useQueryClient();
  const [token] = useState(() => pendingToken('invite'));
  const [step, setStep] = useState<Step>('working');
  const [invite, setInvite] = useState<InvitationPeek | null>(null);
  const started = useRef(false);

  const toLogin = () => {
    if (token) stashToken('invite', token);
    navigate(href('login', {}, { next: href('invite') }), { replace: true });
  };
  const accepted = (scope: InviteScope) => {
    takeStashedToken('invite');
    // The person has just joined something: what was read about their organizations and shares (a guest's visit,
    // an account without an organization) is stale for the next 30 seconds and must not be shown.
    void queryClient.invalidateQueries();
    navigate(destination(scope), { replace: true });
  };
  /** An invitation that cannot be used (accept, peek or sign-up refused it): the token is forgotten, except on a mismatch. */
  const refused = (err: unknown) => {
    const code = err instanceof ApiError ? err.code : null;
    if (code !== 'email_mismatch') takeStashedToken('invite');
    setStep(
      code === 'email_mismatch'
        ? 'mismatch'
        : code === 'expired' || code === 'invitation_expired'
          ? 'expired'
          : code === 'not_found' || code === 'invitation_invalid' || code === 'invalid'
            ? 'invalid'
            : 'error',
    );
  };

  useEffect(() => {
    if (!token || session.status === 'loading' || started.current) return;
    started.current = true;
    if (session.status === 'signed-in' && !session.user.isAnonymous) {
      api.acceptInvitation(token).then(accepted, refused);
      return;
    }
    setStep('checking');
    api.peekInvitation(token).then(
      (peek) => {
        if (peek.accountExists) return toLogin();
        setInvite(peek);
        setStep('signup');
      },
      (err: unknown) => {
        const code = err instanceof ApiError ? err.code : null;
        // The Worker cannot say (offline, busy): the visitor signs in as before, the database decides at the end.
        if (code === 'invitation_invalid' || code === 'invitation_expired' || code === 'invalid') refused(err);
        else toLogin();
      },
    );
    // `accepted`, `refused` and `toLogin` only read the token and set state: the effect runs once (`started`).
  }, [api, token, session]);

  const title = t('guest.inviteTitle');
  if (!token || step === 'invalid') return <Message title={title} body={t('guest.inviteInvalid')} />;
  if (step === 'expired') return <Message title={title} body={t('guest.inviteExpired')} />;
  if (step === 'error') return <Message title={title} body={t('guest.error')} />;
  if (step === 'mismatch') {
    const email = session.status === 'signed-in' ? (session.user.email ?? '') : '';
    return (
      <Message title={title} body={t('guest.emailMismatch', { email })}>
        <Button
          onClick={async () => {
            stashToken('invite', token);
            await api.auth.signOut().catch(() => undefined);
            navigate(href('login', {}, { next: href('invite') }));
          }}
        >
          {t('guest.switchAccount')}
        </Button>
      </Message>
    );
  }
  if (step === 'signup' && invite) {
    return (
      <AuthLayout title={t('invite.title')} hero={t('invite.heroTitle')}>
        <InviteSignup token={token} invite={invite} onAccepted={accepted} onRefused={refused} onSignIn={toLogin} />
      </AuthLayout>
    );
  }
  return (
    <p role="status" className="flex h-dvh items-center justify-center font-sans text-fg-muted">
      {step === 'checking' ? t('invite.checking') : t('guest.accepting')}
    </p>
  );
}
